/**
 * Search Service
 * Fast, pure fuzzy and substring search across Attention Items and AI Agents.
 */

import { AttentionItem, AgentItem } from '../domain/types';

export interface SearchResults {
  items: AttentionItem[];
  agents: AgentItem[];
}

/**
 * Searches items and agents using multi-token case-insensitive matching.
 */
export function searchItems(
  query: string,
  items: AttentionItem[] = [],
  agents: AgentItem[] = []
): SearchResults {
  const trimmed = query.trim().toLowerCase();

  if (!trimmed) {
    return {
      items: [...items],
      agents: [...agents],
    };
  }

  const tokens = trimmed.split(/\s+/).filter(Boolean);

  const matchedItems = items.filter((item) => {
    // Build searchable haystack for the item
    const haystackParts: string[] = [
      item.id,
      item.primary,
      item.ticket || '',
      item.secondary,
      item.title,
      item.desc || '',
      item.ai,
      item.next,
      item.action || '',
      item.type || '',
      item.workflow || '',
      item.fromName || '',
      item.fromEmail || '',
      ...(item.tags || []),
      ...(item.labels || []),
    ];

    if (item.messages) {
      for (const msg of item.messages) {
        haystackParts.push(msg.text, msg.user?.realName || msg.user?.name || '');
      }
    }

    if (item.comments) {
      for (const comment of item.comments) {
        haystackParts.push(comment.body || '', comment.user?.name || '', comment.path || '');
      }
    }

    if (item.body) {
      haystackParts.push(...item.body);
    }

    const haystack = haystackParts.join(' ').toLowerCase();

    // All query tokens must match the item
    return tokens.every((token) => haystack.includes(token));
  });

  const matchedAgents = agents.filter((agent) => {
    const haystack = [
      agent.name || agent.agentName || '',
      agent.ticket || agent.targetTicketKey || '',
      agent.branch || agent.workingBranch || '',
      agent.last || agent.lastActivitySummary || '',
      agent.state,
    ]
      .join(' ')
      .toLowerCase();

    return tokens.every((token) => haystack.includes(token));
  });


  return {
    items: matchedItems,
    agents: matchedAgents,
  };
}

export const SearchService = {
  searchItems,
};

